import { dayjs } from "@/core/utils/date";
import type { IActiveAiScriptRun } from "@/payroll/ai-script/types";
import {
  getAiScriptPayrollRunStatus,
  hasActiveAiScriptRun,
} from "@/payroll/ai-script/utils";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import type {
  PayBreakdownCalculationState,
  PayBreakdownActiveRun,
  PayBreakdownOutput,
  PayBreakdownPeriod,
} from "@/payroll/pay-breakdown/types";
import {
  PAY_BREAKDOWN_CALCULATION_STATE,
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
} from "@/payroll/pay-breakdown/types";
import type {
  EnrichedPayPeriod,
  IPayPeriod,
  IPayrollRun,
} from "@/payroll/payroll.d";
import { isPayPeriodLocked } from "@/payroll/utils/period-helpers";

export const getLatestCompletedLegacyRun = (
  runs: IPayrollRun[] | undefined,
): IPayrollRun | null => {
  if (!runs) return null;

  return (
    [...runs]
      .filter(
        (run) =>
          run.status === PAYROLL_RUN_STATUS.completed && !!run.result_file_url,
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
  );
};

export const applyPayBreakdownStatusToPeriods = (
  periods: PayBreakdownPeriod[],
  payPeriods: EnrichedPayPeriod[],
): PayBreakdownPeriod[] => {
  const payPeriodByStart = new Map(
    payPeriods.map((period) => [period.start_date, period]),
  );

  return periods.map((period) => {
    const payPeriod = payPeriodByStart.get(period.period_start);
    if (!payPeriod) return period;

    const aiStatus = getAiScriptPayrollRunStatus(
      payPeriod.latest_ai_script_run_status,
    );
    const status =
      aiStatus ??
      payPeriod.latest_icm_run_status ??
      payPeriod.latest_run_status;
    const hasPayrollRun =
      !!aiStatus ||
      !!payPeriod.latest_completed_ai_script_run_id ||
      payPeriod.icm_runs_count > 0 ||
      payPeriod.runs_count > 0;

    return {
      ...period,
      has_payroll_run: hasPayrollRun,
      latest_icm_run_status: status,
      is_locked: isPayPeriodLocked(payPeriod),
    };
  });
};

export const getDefaultPayBreakdownPeriod = (
  periods: PayBreakdownPeriod[],
  selectedPeriodStart?: string,
): PayBreakdownPeriod | undefined => {
  if (selectedPeriodStart) return undefined;

  return periods.find((period) => dayjs().isAfter(period.period_end, "day"));
};

export const getPayBreakdownOutput = ({
  hasGeneratedWorkbook,
  selectedAiRunId,
  selectedIcmRunId,
  latestLegacyRun,
}: {
  hasGeneratedWorkbook: boolean;
  selectedAiRunId: string | null;
  selectedIcmRunId: string | null;
  latestLegacyRun: IPayrollRun | null;
}): PayBreakdownOutput => {
  if (hasGeneratedWorkbook && selectedAiRunId) {
    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
      runId: selectedAiRunId,
    };
  }

  if (selectedIcmRunId) {
    return { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.icm, runId: selectedIcmRunId };
  }

  if (latestLegacyRun) {
    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript,
      run: latestLegacyRun,
    };
  }

  return { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.none };
};

export const getPayBreakdownVisibleRunAt = (
  output: PayBreakdownOutput,
  selectedPayPeriod: EnrichedPayPeriod | undefined,
  periodDetail?: Pick<IPayPeriod, "id" | "visible_ai_script_run">,
) => {
  switch (output.type) {
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript: {
      let run = selectedPayPeriod?.visible_ai_script_run;
      if (
        run === undefined &&
        selectedPayPeriod &&
        periodDetail?.id === selectedPayPeriod.id
      ) {
        run = periodDetail.visible_ai_script_run;
      }
      if (run?.id !== output.runId) return null;
      return run.completed_at ?? run.started_at ?? run.created_at;
    }
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.icm:
      return selectedPayPeriod?.latest_icm_run_at;
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript:
      return output.run.created_at;
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.none:
      return null;
  }
};

export const getPayBreakdownActiveRun = (
  period: EnrichedPayPeriod | undefined,
): PayBreakdownActiveRun | null => {
  const activeRun = period?.active_ai_script_runs?.find(
    (run) => run.affects_visible_sheets !== false,
  );
  if (activeRun)
    return {
      at: activeRun.started_at ?? activeRun.created_at,
      isQueued: activeRun.status === "pending",
    };
  if (hasActiveAiScriptRun(period))
    return {
      at: period?.latest_ai_script_run_at,
      isQueued: period?.latest_ai_script_run_status === "pending",
    };
  if (
    period?.latest_icm_run_status === PAYROLL_RUN_STATUS.pending ||
    period?.latest_icm_run_status === PAYROLL_RUN_STATUS.processing
  )
    return {
      at: period.latest_icm_run_at,
      isQueued: period.latest_icm_run_status === PAYROLL_RUN_STATUS.pending,
    };
  if (
    period?.latest_run_status === PAYROLL_RUN_STATUS.pending ||
    period?.latest_run_status === PAYROLL_RUN_STATUS.processing
  )
    return {
      at: period.latest_run_at,
      isQueued: period.latest_run_status === PAYROLL_RUN_STATUS.pending,
    };
  return null;
};

export const hasActiveAiRunAffectingVisibleSheets = (
  activeRuns: IActiveAiScriptRun[] | undefined,
) => (activeRuns ?? []).some((run) => run.affects_visible_sheets !== false);

interface GetPayBreakdownCalculationStateParams {
  hasSelectedPeriod: boolean;
  hasActiveRun: boolean;
  hasOutput: boolean;
  activeRunAffectsVisibleOutput: boolean;
}

export const getPayBreakdownCalculationState = ({
  hasSelectedPeriod,
  hasActiveRun,
  hasOutput,
  activeRunAffectsVisibleOutput,
}: GetPayBreakdownCalculationStateParams): PayBreakdownCalculationState => {
  if (!hasSelectedPeriod || !hasActiveRun) {
    return PAY_BREAKDOWN_CALCULATION_STATE.idle;
  }

  if (!hasOutput) {
    return activeRunAffectsVisibleOutput
      ? PAY_BREAKDOWN_CALCULATION_STATE.initial
      : PAY_BREAKDOWN_CALCULATION_STATE.background;
  }

  return activeRunAffectsVisibleOutput
    ? PAY_BREAKDOWN_CALCULATION_STATE.rerun
    : PAY_BREAKDOWN_CALCULATION_STATE.background;
};

export const getLegacyRunFilename = (run: IPayrollRun) =>
  run.result_file_metadata?.filename ?? "payroll.xlsx";
